import React from 'react'
import { View } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import styled from 'styled-components/native'
import { NativeStackScreenProps } from '@react-navigation/native-stack'
import { LookupOrganization } from '@dialogue/scribe'

// Components
import { ResponsiveView } from 'APP/Converse/Layout'
import Typography from 'APP/Converse/Typography'
import Button from 'APP/Converse/Button'
import ListItem from 'APP/Converse/ListItem'
import ChallengedInvitationFooter from 'APP/Components/ChallengedInvitationFooter'
import BottomDrawer from 'APP/Converse/BottomDrawer'
import Spinner from 'APP/Components/Spinner'

// Services
import I18n from 'APP/I18n'
import Analytics from 'APP/Services/Analytics'

// State management
import { useDispatch, useSelector } from 'APP/Hooks/redux'
import PatientActions from 'APP/Redux/PatientRedux'
import { eligibilitySearchSelectors, eligibilitySearchActions } from 'APP/Store/EligibilitySearch'

export type OrganizationListProps = {
  organizations: LookupOrganization[]
  handleConfirmationModal: (org: LookupOrganization, index: number) => void
}

const ScreenContainer = styled(SafeAreaView).attrs({
  edges: ['bottom', 'left', 'right'],
})`
  flex: 1;
`

const ContentContainer = styled(ResponsiveView)`
  gap: ${({ theme }) => theme.metrics.baseMargin}px;
  padding: ${({ theme }) => theme.metrics.baseMargin}px;
  flex: 1;
  justify-content: space-between;
`

const InnerContentContainer = styled(ResponsiveView)`
  gap: ${({ theme }) => theme.metrics.baseMargin}px;
`
const Title = styled(Typography)`
  margin-bottom: ${({ theme }) => theme.metrics.baseMargin}px;
`

export const OrganizationList = ({
  organizations,
  handleConfirmationModal,
}: OrganizationListProps) => (
  <View>
    <Title variant="h1">{I18n.t('TrustBasedEligibilityResults.title')}</Title>
    {organizations.map((org, index) => (
      <ListItem
        key={`TBE-SEARCH-RESULT-${org.id}`}
        testID={`TBE_SEARCH_RESULT_${org.id}`}
        title={org.display_name ? org.display_name[I18n.baseLocale] : org.name}
        onPress={() => {
          handleConfirmationModal(org, index)
        }}
      />
    ))}
  </View>
)

export type TrustBasedEligibilityResultsScreenProps = NativeStackScreenProps<
  ReactNavigation.RootParamList,
  'trustBasedEligibilityResults'
>

const TrustBasedEligibilityResultsScreen = ({
  navigation,
  route,
}: TrustBasedEligibilityResultsScreenProps) => {
  const [selectedOrganization, setSelectedOrganization] = React.useState<LookupOrganization | null>(
    null
  )
  const [isModalVisible, setIsModalVisible] = React.useState(false)

  const dispatch = useDispatch()

  const analyticsName = 'trust_based_eligibility_results'
  const enrolLoading = useSelector(eligibilitySearchSelectors.selectEnrolWithOrganizationIdLoading)
  const enrolSuccess = useSelector(eligibilitySearchSelectors.selectEnrolWithOrganizationIdSuccess)
  const enrolError = useSelector(eligibilitySearchSelectors.selectEnrolWithOrganizationIdError)

  // We need to determine if the member is still onboarding or
  // in the invitation queue to decide whether to show the "skip" footer
  // This flow follows the same pattern as the EligibilityClaimScreen
  const { challengedInvitationQueue, isOnboarding } = useSelector((state) => state.patient)

  const isInChallengedQueue = (challengedInvitationQueue ?? []).length > 0

  const onboardingDeferredEnrolmentComplete = () => {
    dispatch(PatientActions.onboardingDeferredEnrolmentComplete())
  }

  const handleSearchAgain = () => {
    Analytics.trackEvent('button_click', {
      button_value: `${analyticsName}_search_again`,
    })

    navigation.navigate('trustBasedEligibilitySearch')
  }

  const handleConfirmationModal = (organization: LookupOrganization, index: number) => {
    Analytics.trackEvent('button_click', {
      button_value: `${analyticsName}_organization_selected`,
      position_in_list: index,
      organization_name: organization.name,
    })
    setSelectedOrganization(organization)
    setIsModalVisible(true)
  }

  const handleOrganizationEnroll = (organization: LookupOrganization | null) => {
    if (enrolLoading || !organization) return
    dispatch(eligibilitySearchActions.enrolWithOrganizationId(organization.id))
  }

  const handleCloseModal = () => {
    setIsModalVisible(false)
    setSelectedOrganization(null)
  }

  const handleContactSupport = () => {
    Analytics.trackEvent('button_click', {
      button_value: `${analyticsName}_contact_support`,
    })

    navigation.navigate('helpCentre', { isRegistrationPage: true, screenName: `Registration - ${route.name}` })
  }

  React.useEffect(() => {
    if (enrolSuccess || enrolError) {
      handleCloseModal()
    }
  }, [enrolSuccess, enrolError])

  const results = useSelector(eligibilitySearchSelectors.selectEligibilitySearchResults) ?? []

  return (
    <ScreenContainer testID="TRUST_BASED_ELIGIBILITY_RESULTS_SCREEN">
      <ContentContainer>
        <InnerContentContainer>
          {results.length > 0 ? (
            <OrganizationList
              organizations={results}
              handleConfirmationModal={handleConfirmationModal}
            />
          ) : (
            <>
              <Typography variant="h1" testID="TRUST_BASED_ELIGIBILITY_RESULTS_NO_RESULTS">
                {I18n.t('TrustBasedEligibilityResults.noResultsTitle')}
              </Typography>
              <Typography variant="bodyNormal" color="text">
                {I18n.t('TrustBasedEligibilityResults.noResultsSubtitle')}
              </Typography>
              <Typography variant="bodyNormal" color="text">
                {I18n.t('TrustBasedEligibilityResults.noResultsFurtherInfo')}{' '}
                <Typography
                  variant="link"
                  color="text"
                  onPress={handleContactSupport}
                  testID="TRUST_BASED_ELIGIBILITY_RESULTS_CONTACT_SUPPORT"
                >
                  {I18n.t('TrustBasedEligibilityResults.noResultsContactSupport')}
                </Typography>
              </Typography>
            </>
          )}
          <Button
            title={I18n.t('TrustBasedEligibilityResults.button')}
            onPress={handleSearchAgain}
            widthVariant="full"
            testID="TRUST_BASED_ELIGIBILITY_SEARCH_AGAIN_BUTTON"
            variant="tertiary"
          />
        </InnerContentContainer>
        {(isOnboarding || isInChallengedQueue) && (
          <ChallengedInvitationFooter
            analyticsName={analyticsName}
            onPress={() => isOnboarding && onboardingDeferredEnrolmentComplete()}
          />
        )}
      </ContentContainer>
      <BottomDrawer
        isVisible={isModalVisible}
        onClose={handleCloseModal}
        title={I18n.t('TrustBasedEligibilityResults.confirmationModal.title', {
          organization: selectedOrganization?.display_name
            ? selectedOrganization.display_name[I18n.baseLocale]
            : selectedOrganization?.name,
          interpolation: { escapeValue: false },
        })}
        subtitle={I18n.t('TrustBasedEligibilityResults.confirmationModal.subtitle')}
        testID="TBE_ENROLLMENT_CONFIRMATION_MODAL"
        primaryButtonProps={{
          title: I18n.t('TrustBasedEligibilityResults.confirmationModal.confirmButton'),
          onPress: () => handleOrganizationEnroll(selectedOrganization),
          disabled: enrolLoading || !selectedOrganization,
          testID: 'TBE_ENROLLMENT_CONFIRM_BUTTON',
        }}
        secondaryButtonProps={{
          title: I18n.t('TrustBasedEligibilityResults.confirmationModal.cancelButton'),
          onPress: handleCloseModal,
          disabled: enrolLoading,
          testID: 'TBE_ENROLLMENT_CANCEL_BUTTON',
        }}
      />
      {enrolLoading ? <Spinner visible /> : null}
    </ScreenContainer>
  )
}

export default TrustBasedEligibilityResultsScreen
